import { Action } from '@tribeplatform/gql-client/global-types'
import { type ClientError } from '@tribeplatform/gql-client/lib'
import { RoleType } from '@tribeplatform/gql-client/types'

import { useQuery, UseQueryOptions } from '../../lib/index.js'
import { useTribeClient } from '../../useTribeClient.js'
import { getPingSessionKey } from '../../utils/keys/ping-session.keys.js'

import { useAuthMember } from './useAuthMember.js'

type UsePingSessionOptions = Omit<
  UseQueryOptions<Action, ClientError>,
  'refetchInterval'
>

/**
 * The advertised limit is 1req/5m
 * The hard limit is 3req/5m
 * Setting the interval to 4 minutes should give a good balance.
 * The rate-limit errors are already suppressed in the client and should not trigger any errors.
 */
const FOUR_MINUTES_IN_MS = 4 * 60 * 1000

export const usePingSession = (options?: {
  useQueryOptions?: UsePingSessionOptions
}) => {
  const { useQueryOptions } = options || {}
  const { enabled = true } = useQueryOptions || {}
  const { data: authMember } = useAuthMember()
  const { client } = useTribeClient()

  return useQuery<Action, ClientError>(
    getPingSessionKey(),
    () => client.auth.pingSession(),
    {
      ...useQueryOptions,
      refetchInterval: FOUR_MINUTES_IN_MS,
      enabled:
        enabled && authMember && authMember?.role?.type !== RoleType.guest,
    },
  )
}
